/**
 * Position sources, the thing the app plugs a GPS into.
 *
 * A PositionSource produces NMEA 0183 lines and nothing else. That's the whole
 * contract. Today the only implementation is the simulator, which runs the
 * teaching boat model and emits the sentences a real receiver would. Later a
 * WebSocket or SignalK source implements the same interface and the rest of the
 * app, which only ever sees parsed fixes, doesn't change.
 *
 * The transport here is just an in-process callback. A real source would open a
 * socket and forward whatever the wire gives it through the same onSentence
 * channel.
 */
import { simStep, type SimStepOutput } from '@/ownship/sim';
import type { TideCycle } from '@/environment';
import { encodeGGA, encodeGST, encodeRMC } from './nmea';

/** Anything that feeds NMEA sentences to the app. */
export interface PositionSource {
  /** A short id for the kind of source, e.g. 'sim' or 'nmea-ws'. */
  readonly kind: string;
  /** Begin producing sentences. */
  start(): void;
  /** Stop producing and release any transport. */
  stop(): void;
  /** Subscribe to raw NMEA lines. Returns an unsubscribe function. */
  onSentence(cb: (line: string) => void): () => void;
}

/** The boat-command and environment inputs the simulator reads each tick. */
export interface SimInputs {
  /** Heading through the water, degrees true (the helm command). */
  headingDeg: number;
  /** Speed through the water, knots (the throttle command). */
  stwKn: number;
  /** Leeway angle, degrees (water-track offset from heading). */
  leewayDeg?: number;
  /** Scenario tide. */
  tide: TideCycle;
  /** Time-acceleration factor (1 = real time). */
  rate: number;
}

export interface SimulatedGnssOptions {
  /** Where the boat starts, [lon, lat]. */
  startPosition: [number, number];
  /** Scenario clock at start, hours since t=0. */
  startHour: number;
  /** Live getter for the boat-command + environment inputs. */
  getInputs: () => SimInputs;
  /** Emit interval in real milliseconds. Default 1000 (1 Hz, like a real GPS). */
  intervalMs?: number;
  /**
   * Called after each step with the scenario clock, so the app can keep its
   * tide displays in sync. This is the simulator's own world, not part of the
   * GNSS wire, a real source wouldn't have it.
   */
  onClock?: (tHour: number) => void;
}

/** Clamp a number into [lo, hi]. */
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Fixed reference epoch for the simulated UTC stamp. We anchor scenario time to
 * a real date so RMC carries a valid date and time, and so track time-marks
 * read in scenario time (they're measured relative to the first point, so the
 * constant offset cancels). A real receiver would stamp wall-clock UTC instead.
 */
const SIM_EPOCH_MS = Date.UTC(2025, 0, 1, 0, 0, 0);

export class SimulatedGnssSource implements PositionSource {
  readonly kind = 'sim';

  private listeners = new Set<(line: string) => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private pos: [number, number];
  private tHour: number;
  private readonly intervalMs: number;
  private readonly getInputs: () => SimInputs;
  private readonly onClock?: (tHour: number) => void;
  /** Real seconds elapsed, the phase for the wandering accuracy model. */
  private elapsedSec = 0;
  /** A per-source random phase so two sources don't wander in lockstep. */
  private readonly accPhase = Math.random() * Math.PI * 2;

  constructor(opts: SimulatedGnssOptions) {
    this.pos = opts.startPosition;
    this.tHour = opts.startHour;
    this.intervalMs = opts.intervalMs ?? 1000;
    this.getInputs = opts.getInputs;
    this.onClock = opts.onClock;
  }

  start(): void {
    if (this.timer !== null) return;
    // Emit an immediate fix so the app "acquires" position at the start point
    // without waiting a whole interval.
    this.tick(0);
    this.timer = setInterval(() => this.tick(this.intervalMs / 1000), this.intervalMs);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  onSentence(cb: (line: string) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  /**
   * Advance the boat by `dtSec` real seconds and emit one RMC + one GGA.
   * Exposed (rather than purely internal to the timer) so tests can drive the
   * source deterministically a step at a time.
   */
  tick(dtSec: number): SimStepOutput {
    const inp = this.getInputs();
    const step = simStep({
      position: this.pos,
      headingDeg: inp.headingDeg,
      stwKn: inp.stwKn,
      leewayDeg: inp.leewayDeg,
      tide: inp.tide,
      tHour: this.tHour,
      dtSec,
      rate: inp.rate,
    });
    this.pos = step.position;
    this.tHour = step.tHour;

    const utc = new Date(SIM_EPOCH_MS + step.tHour * 3_600_000);

    // Receiver accuracy model. A real GPS reports its own position-error
    // statistics (GST), or we estimate from HDOP. Here the simulated receiver
    // produces a gently varying horizontal error and derives consistent HDOP
    // and satellite counts, so GGA and GST tell the same story. This lives on
    // the source (the "receiver"), not the boat physics, and rides the feed
    // exactly as a real receiver's would.
    this.elapsedSec += dtSec;
    const acc = this.sampleAccuracyM();
    const hdop = clamp(acc / 4.5, 0.6, 3.2);
    const satellites = Math.round(clamp(13 - acc * 0.55, 5, 14));
    const sigma = acc / Math.SQRT2; // split equally across lat/lon so RSS = acc

    // GGA and GST first, then RMC commits the fix with both already in hand.
    this.emit(encodeGGA({ position: step.position, utc, fixQuality: 1, satellites, hdop }));
    this.emit(
      encodeGST({
        utc,
        rmsM: acc * 0.7,
        semiMajorM: acc * 1.15,
        semiMinorM: acc * 0.72,
        orientDeg: (this.elapsedSec * 6) % 360,
        latErrM: sigma,
        lonErrM: sigma,
        altErrM: acc * 1.6,
      }),
    );
    this.emit(encodeRMC({ position: step.position, sogKn: step.sogKn, cogDeg: step.cogDeg, utc }));

    this.onClock?.(step.tHour);
    return step;
  }

  /**
   * A gently varying horizontal position error in metres. Two slow sinusoids of
   * different periods plus a touch of noise drift the value the way a real
   * receiver's accuracy wanders as the satellite geometry changes, rather than
   * a flat, fake-looking constant. Clamped to a believable band.
   */
  private sampleAccuracyM(): number {
    const t = this.elapsedSec;
    const base = 3.4;
    const slow = 1.8 * Math.sin(t / 37 + this.accPhase);
    const fast = 0.9 * Math.sin(t / 8.5 + this.accPhase * 1.7);
    // Occasional degradation episode (poor satellite geometry or multipath):
    // near zero most of the time, briefly swelling the error well up every
    // minute or so, so the poor-accuracy alarm has a real event to catch.
    const swell = Math.pow(Math.max(0, Math.sin(t / 11 + this.accPhase * 0.6)), 6) * 20;
    const noise = (Math.random() - 0.5) * 0.6;
    return clamp(base + slow + fast + swell + noise, 1.2, 30);
  }

  private emit(line: string): void {
    for (const cb of this.listeners) cb(line);
  }
}
